import React, { useEffect, useRef, useState } from "react";
import { base44, getActiveOrgId } from "@/api/base44Client";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganisation } from "@/lib/OrganisationContext";
import { ExternalLink, Loader2, Maximize2, Mic, Send, Square, Volume2, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useAccessibility } from "@/lib/accessibilityContext";

const SUGGESTIONS = [
  "What experiences have shaped your perspective?",
  "What would you like organisations to understand?",
  "What helps you feel included at work and in services?",
  "What practical change would make the biggest difference?",
];

function PersonaAvatar({ persona, className }) {
  if (persona.avatar_url)
    return (
      <img
        data-decorative-image="true"
        src={persona.avatar_url}
        alt={`${persona.name || persona.slug} persona`}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    );
  const initials = (persona.name || persona.slug || "?")
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      data-decorative-image="true"
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white ${className}`}
    >
      {initials}
    </div>
  );
}

export default function PersonaChat({
  open,
  onClose,
  initialMode = "text",
  persona = {
    id: "hannah",
    slug: "hannah",
    name: "Hannah",
    descriptor: "Lived experience persona",
  },
}) {
  const { prefs } = useAccessibility();
  const { selectedOrg, tenantRevision } = useOrganisation();
  const personaName = persona.name || persona.slug;
  const voiceMode = initialMode === "voice";
  const tenantKey = selectedOrg?.id || getActiveOrgId() || "none";
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [conversationError, setConversationError] = useState("");
  const [panelWidth, setPanelWidth] = useState(440);
  const [retryGeneration, setRetryGeneration] = useState(0);
  const scrollRef = useRef(null);
  const sentCountRef = useRef(0);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const speakTimerRef = useRef(null);
  const lastSpokenContentRef = useRef("");
  const awaitingReplyRef = useRef(false);
  const speechReqIdRef = useRef(0);
  const speechControllerRef = useRef(null);
  const generationRef = useRef(0);
  const activeRef = useRef(false);
  const busyRef = useRef(false);

  function stopResources() {
    generationRef.current += 1;
    activeRef.current = false;
    busyRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    speechReqIdRef.current += 1;
    speechControllerRef.current?.abort();
    speechControllerRef.current = null;
    if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
    speakTimerRef.current = null;
    awaitingReplyRef.current = false;
    if (typeof window !== "undefined" && window.speechSynthesis)
      window.speechSynthesis.cancel();
    setListening(false);
    setSpeaking(false);
  }

  function closeChat() {
    stopResources();
    onClose();
  }

  async function handleSpeak(text, expectedGeneration = generationRef.current) {
    if (!prefs.audioEnabled) { setConversationError("Turn on optional app audio in Accessibility settings to play speech."); return; }
    const requestId = ++speechReqIdRef.current;
    speechControllerRef.current?.abort();
    const speechController = new AbortController();
    speechControllerRef.current = speechController;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeaking(true);
    try {
      const result = await base44.integrations.Core.GenerateSpeech(
        {
          text: text.substring(0, 5000),
          voice: persona.voice || "default",
          language_code: "en",
        },
        { signal: speechController.signal },
      );
      if (
        !activeRef.current ||
        document.documentElement.getAttribute("data-app-audio") !== "on" ||
        expectedGeneration !== generationRef.current ||
        requestId !== speechReqIdRef.current
      )
        return;
      if (!result?.url) {
        setSpeaking(false);
        return;
      }
      const audio = new Audio(result.url);
      audio.playbackRate = prefs.speechRate || 1;
      audioRef.current = audio;
      audio.onended = () => {
        if (
          !activeRef.current ||
          expectedGeneration !== generationRef.current ||
          requestId !== speechReqIdRef.current
        )
          return;
        setSpeaking(false);
        if (audioRef.current === audio) audioRef.current = null;
        if (voiceMode && open) handleMic();
      };
      audio.onerror = () => {
        if (
          expectedGeneration !== generationRef.current ||
          requestId !== speechReqIdRef.current
        )
          return;
        setSpeaking(false);
        if (audioRef.current === audio) audioRef.current = null;
      };
      audio.play().catch(() => {
        if (
          expectedGeneration !== generationRef.current ||
          requestId !== speechReqIdRef.current
        )
          return;
        setSpeaking(false);
        if (audioRef.current === audio) audioRef.current = null;
      });
    } catch (error) {
      if (
        !speechController.signal.aborted &&
        document.documentElement.getAttribute("data-app-audio") === "on" &&
        requestId === speechReqIdRef.current &&
        expectedGeneration === generationRef.current
      ) {
        setSpeaking(false);
        setConversationError(error.message || "Could not generate speech.");
      }
    } finally {
      if (speechControllerRef.current === speechController)
        speechControllerRef.current = null;
    }
  }

  function handleMic() {
    if (!prefs.audioEnabled) { setConversationError("Turn on optional app audio in Accessibility settings before using the microphone."); return; }
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setConversationError(
        "Speech recognition is not supported in this browser. Please use Chrome or Edge.",
      );
      return;
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    if (!conversation || initializing || loading || !activeRef.current) return;
    const expectedGeneration = generationRef.current;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-GB";
    recognition.continuous = true;
    recognition.interimResults = true;
    let bestTranscript = "";
    recognition.onresult = (event) => {
      let interim = "";
      for (
        let index = event.resultIndex;
        index < event.results.length;
        index += 1
      ) {
        const transcript = event.results[index][0].transcript;
        if (event.results[index].isFinal) bestTranscript += transcript;
        else interim += transcript;
      }
      if (activeRef.current && expectedGeneration === generationRef.current)
        setInput(bestTranscript + interim);
    };
    recognition.onend = () => {
      if (expectedGeneration !== generationRef.current || !activeRef.current)
        return;
      setListening(false);
      recognitionRef.current = null;
      const text = bestTranscript.trim();
      if (text) void handleSend(text, expectedGeneration);
      else setInput("");
    };
    recognition.onerror = () => {
      if (expectedGeneration !== generationRef.current) return;
      setListening(false);
      recognitionRef.current = null;
      setConversationError("Speech recognition stopped. Try again.");
    };
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  }

  useEffect(() => {
    const stopAudio = () => {
      recognitionRef.current?.abort(); recognitionRef.current = null;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; audioRef.current = null; }
      speechControllerRef.current?.abort(); speechControllerRef.current = null;
      window.speechSynthesis?.cancel(); setListening(false); setSpeaking(false);
    };
    window.addEventListener("inclusivecore:stop-audio", stopAudio);
    return () => window.removeEventListener("inclusivecore:stop-audio", stopAudio);
  }, []);

  useEffect(() => {
    stopResources();
    const targetGeneration = ++generationRef.current;
    activeRef.current = open;
    setConversation(null);
    setMessages([]);
    setInput("");
    setLoading(false);
    setInitializing(Boolean(open));
    setConversationError("");
    sentCountRef.current = 0;
    lastSpokenContentRef.current = "";
    if (!open) return undefined;
    let unsub = () => {};
    let cancelled = false;
    (async () => {
      try {
        const nextConversation = await base44.agents.createConversation({
          agent_name: persona.slug,
          metadata: {
            name: personaName,
            persona_id: persona.id,
            organisation_id: tenantKey,
          },
        });
        if (
          cancelled ||
          !activeRef.current ||
          targetGeneration !== generationRef.current
        )
          return;
        setConversation(nextConversation);
        setMessages(nextConversation.messages || []);
        unsub = base44.agents.subscribeToConversation(
          nextConversation.id,
          (data) => {
            if (
              cancelled ||
              !activeRef.current ||
              targetGeneration !== generationRef.current
            )
              return;
            const nextMessages = data.messages || [];
            setMessages(nextMessages);
            if (nextMessages.length > sentCountRef.current) {
              busyRef.current = false;
              setLoading(false);
            }
          },
        );
      } catch (error) {
        if (!cancelled && targetGeneration === generationRef.current)
          setConversationError(
            error.message || "Could not start this conversation.",
          );
      } finally {
        if (!cancelled && targetGeneration === generationRef.current)
          setInitializing(false);
      }
    })();
    return () => {
      cancelled = true;
      unsub();
      stopResources();
    };
  }, [
    open,
    persona.id,
    persona.slug,
    personaName,
    tenantKey,
    tenantRevision,
    initialMode,
    retryGeneration,
  ]);

  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!voiceMode || !awaitingReplyRef.current) return undefined;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return undefined;
    if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
    speakTimerRef.current = setTimeout(() => {
      speakTimerRef.current = null;
      if (last.content && last.content !== lastSpokenContentRef.current) {
        lastSpokenContentRef.current = last.content;
        awaitingReplyRef.current = false;
        void handleSpeak(last.content, generationRef.current);
      }
    }, 1500);
    return () => {
      if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
      speakTimerRef.current = null;
    };
  }, [voiceMode, messages]);

  async function handleSend(text, expectedGeneration = generationRef.current) {
    const content = (text ?? input).trim();
    if (
      !content ||
      !conversation ||
      loading ||
      busyRef.current ||
      !activeRef.current ||
      expectedGeneration !== generationRef.current
    )
      return;
    busyRef.current = true;
    setInput("");
    setLoading(true);
    setConversationError("");
    awaitingReplyRef.current = true;
    sentCountRef.current = messages.length;
    try {
      await base44.agents.addMessage(conversation, { role: "user", content });
    } catch (error) {
      if (expectedGeneration === generationRef.current && activeRef.current) {
        busyRef.current = false;
        setLoading(false);
        setConversationError(error.message || "Could not send that message.");
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) closeChat();
      }}
    >
      <DialogContent
        aria-label={`Chat with ${personaName}`}
        hideClose
        style={{ width: `min(${panelWidth}px, 100vw)` }}
        className="persona-chat fixed inset-y-0 right-0 left-auto top-0 flex h-full max-w-full translate-x-0 translate-y-0 flex-col gap-0 overflow-y-auto rounded-none bg-white p-0 shadow-2xl"
      >
        <div className="flex items-center gap-3 border-b border-slate-100 bg-[#6366F1] px-5 py-4">
          <PersonaAvatar persona={persona} className="h-10 w-10" />
          <div className="min-w-0 flex-1">
          <DialogTitle className="text-base font-bold text-white">
              Chat with {personaName}
            </DialogTitle>
            <p className="text-xs text-white/80">
              {persona.descriptor || "Lived experience persona"}
            </p>
          </div>
          <button type="button" aria-label="Open chat in a new window" onClick={() => window.open(`${window.location.origin}/personas?chatPersona=${encodeURIComponent(persona.slug)}`, "persona-chat", "popup,width=520,height=760,resizable=yes")} className="persona-chat-close inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"><ExternalLink className="h-5 w-5" aria-hidden="true" /></button>
          <button type="button" aria-label="Resize chat panel" onClick={() => setPanelWidth((width) => width >= 720 ? 440 : width + 140)} className="persona-chat-close inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"><Maximize2 className="h-5 w-5" aria-hidden="true" /></button>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close chat"
              className="persona-chat-close min-h-11 min-w-11 rounded-lg text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </DialogClose>
        </div>
        <DialogDescription className="sr-only">
          Conversation with {personaName}. Close with Escape.
        </DialogDescription>
        <div
          ref={scrollRef}
          className="flex-1 space-y-4 overflow-y-auto bg-slate-50 px-4 py-4"
        >
          {initializing && (
            <div
              className="flex items-center justify-center py-8"
              role="status"
              aria-live="polite"
            >
              <div
                className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-[#6366F1]"
                aria-hidden="true"
              />
            </div>
          )}
          {conversationError && (
            <div
              className="space-y-3 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800"
              role="alert"
            >
              <p>{conversationError}</p>
              {!conversation && (
                <button
                  type="button"
                  className="min-h-11 rounded-lg border border-red-700 px-4 py-2 font-semibold"
                  onClick={() => setRetryGeneration((value) => value + 1)}
                >
                  Retry conversation
                </button>
              )}
            </div>
          )}
          {messages.length === 0 && !initializing && !conversationError && (
            <div className="space-y-4">
              <div className="flex justify-start">
                <PersonaAvatar
                  persona={persona}
                  className="mr-2 mt-1 h-7 w-7"
                />
                <div className="max-w-[80%] rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <p className="text-sm text-slate-700">
                    Hi, I'm {personaName}. Ask me about my lived experience and
                    how organisations can improve inclusion.
                  </p>
                </div>
              </div>
              <p className="px-1 text-xs text-slate-400">Try asking:</p>
              <div className="space-y-2">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => handleSend(suggestion)}
                    disabled={!conversation || loading}
                    className="block min-h-11 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6366F1] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((message, index) => (
            <div
              key={message.id || index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {message.role !== "user" && (
                <PersonaAvatar
                  persona={persona}
                  className="mr-2 mt-1 h-7 w-7"
                />
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${message.role === "user" ? "rounded-tr-sm bg-[#6366F1] font-medium text-white" : "rounded-tl-sm border border-slate-200 bg-white text-slate-800 shadow-sm"}`}
              >
                {message.role === "user" ? (
                  <p>{message.content}</p>
                ) : (
                  <>
                    <ReactMarkdown className="prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      {message.content}
                    </ReactMarkdown>
                    <button
                      type="button"
                      onClick={() => handleSpeak(message.content)}
                      disabled={speaking}
                      aria-label="Listen to this response"
                      className="mt-2 flex min-h-11 items-center gap-1.5 text-xs text-slate-400 transition-colors hover:text-[#6366F1] disabled:opacity-40"
                    >
                      {speaking ? (
                        <Loader2
                          className="h-3 w-3 animate-spin"
                          aria-hidden="true"
                        />
                      ) : (
                        <Volume2 className="h-3 w-3" aria-hidden="true" />
                      )}
                      {speaking ? "Generating audio…" : "Listen"}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 pl-9 text-xs text-slate-400">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#6366F1]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#6366F1]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-[#6366F1]" />
              </div>
              {personaName} is responding…
            </div>
          )}
        </div>
        <div className="border-t border-slate-100 bg-white px-4 py-3">
          {voiceMode ? (
            <div className="space-y-2">
              <div className="min-h-[42px] rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                {input || (
                  <span className="text-slate-400">
                    {listening
                      ? "Listening…"
                      : "Press Talk, then ask your question"}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleMic}
                disabled={!conversation || initializing || loading || speaking}
                aria-label={
                  listening ? "Stop and send" : `Talk to ${personaName}`
                }
                aria-pressed={listening}
                className={`min-h-11 w-full rounded-xl py-3 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6366F1] disabled:cursor-not-allowed disabled:opacity-50 ${listening ? "bg-red-500 text-white" : "bg-[#6366F1] text-white"}`}
              >
                {listening ? (
                  <>
                    <Square
                      className="mr-2 inline h-4 w-4"
                      fill="currentColor"
                      aria-hidden="true"
                    />
                    Stop &amp; send
                  </>
                ) : speaking ? (
                  <>
                    <Loader2
                      className="mr-2 inline h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                    {personaName} is speaking…
                  </>
                ) : (
                  <>
                    <Mic className="mr-2 inline h-4 w-4" aria-hidden="true" />
                    Talk to {personaName}
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void handleSend();
                  }
                }}
                placeholder={`Ask ${personaName} anything…`}
                rows={1}
                aria-label="Type your message"
                disabled={
                  !conversation ||
                  initializing ||
                  loading ||
                  !!conversationError
                }
                className="min-h-11 flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6366F1]"
              />
              <button
                type="button"
                onClick={handleMic}
                disabled={!conversation || initializing || loading}
                aria-label={listening ? "Stop listening" : "Speak your message"}
                aria-pressed={listening}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6366F1] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Mic className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => void handleSend()}
                disabled={
                  !input.trim() || loading || !conversation || initializing
                }
                aria-label="Send message"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl bg-[#6366F1] text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#6366F1] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
